import PropTypes from "prop-types";
import DownloadingIcon from "@mui/icons-material/Downloading";
import { Button, CircularProgress, Tooltip } from "@mui/material";
import { useState } from "react";
import WarningDialog from "../fundamentals/WarningDialog.jsx";
import SuccessDialog from "../fundamentals/SuccessDialog";
import { IAM_CONTRACT, TWU_CONTRACT } from "../../util/constants";
import { getUnionContractGivenWorkgroup } from "../../util/workgroupMapper";
import {
  isBalanceImportButtonDisabled,
  parseIAMDeclarationBalanceImportCSV,
  parseTWUDeclarationBalanceImportCSV,
  sendDeclarationImportFileToS3,
  validateIAMDeclarationBalancesCSV,
  validateTWUDeclarationBalancesCSV,
} from "../../util/declarations/declarationUtils";
import {
  IAM_DECLARATION_BALANCES_IMPORT_ERROR_MESSAGE,
  TWU_DECLARATION_BALANCES_IMPORT_ERROR_MESSAGE,
} from "../../util/bid/messageConstants.js";

const DeclarationBalanceImport = (props) => {
  const [balancesCSVErrors, setBalancesCSVErrors] = useState(null);
  const [showBalanceImportSuccessDialog, setShowBalanceImportSuccessDialog] = useState(false);
  const [isBalanceImporting, setIsBalanceImporting] = useState(false);

  const handleBalancesImport = (event) => {
    try {
      setIsBalanceImporting(true);
      const file = event.target.files[0];
      // workaround to select same file again.
      event.target.value = "";
      if (!file) return;
      if (!file.name.endsWith(".csv")) {
        setBalancesCSVErrors(["Invalid file type. Please upload a CSV file."]);
        return;
      }
      const reader = new FileReader();

      reader.onload = async (e) => {
        const response = await sendDeclarationImportFileToS3(
          props.station,
          props.workgroup,
          props.declarationYear,
          file.name,
          e.target.result
        );
        if (response.status === 200) {
          const csvText = e.target.result;
          if (getUnionContractGivenWorkgroup(props.workgroup) === TWU_CONTRACT) {
            processTWUDeclarationBalanceImport(csvText);
          } else if (getUnionContractGivenWorkgroup(props.workgroup) === IAM_CONTRACT) {
            processIAMDeclarationBalanceImport(csvText);
          } else {
            console.error("Invalid Contract");
          }
          setIsBalanceImporting(false);
        } else {
          setBalancesCSVErrors(["Balance import failed, please retry"]);
          setIsBalanceImporting(false);
        }
      };
      reader.readAsText(file);
    } catch {
      if (getUnionContractGivenWorkgroup(props.workgroup) === TWU_CONTRACT) {
        setBalancesCSVErrors(TWU_DECLARATION_BALANCES_IMPORT_ERROR_MESSAGE);
      } else if (getUnionContractGivenWorkgroup(props.workgroup) === IAM_CONTRACT) {
        setBalancesCSVErrors(IAM_DECLARATION_BALANCES_IMPORT_ERROR_MESSAGE);
      } else {
        setBalancesCSVErrors("Error during Balances import");
      }
      setIsBalanceImporting(false);
    }
  };

  const processTWUDeclarationBalanceImport = (csvText) => {
    const { data, error } = parseTWUDeclarationBalanceImportCSV(csvText);
    if (error) {
      setBalancesCSVErrors([error]);
      return;
    }

    const errors = validateTWUDeclarationBalancesCSV(data, props.declarations);
    if (errors.length) {
      setBalancesCSVErrors(errors);
      return;
    }

    const eidToBalanceMap = Object.fromEntries(
      data.map((row) => [
        row["eid"],
        { projectedVacationBalance: row["projected vacation hours"], projectedFtoBalance: row["projected fto hours"] },
      ])
    );

    const updatedDeclarations = props.declarations.map((eachEmp) => ({
      ...eachEmp,
      ...(eidToBalanceMap[eachEmp.eid] || {}),
    }));

    props.setDeclarations(updatedDeclarations);
    setShowBalanceImportSuccessDialog(true);
  };

  const processIAMDeclarationBalanceImport = (csvText) => {
    const { data, error } = parseIAMDeclarationBalanceImportCSV(csvText);

    if (error) {
      setBalancesCSVErrors([error]);
      return;
    }

    const errors = validateIAMDeclarationBalancesCSV(data, props.declarations);
    if (errors.length) {
      setBalancesCSVErrors(errors);
      return;
    }

    const eidToBalanceMap = Object.fromEntries(
      data.map((row) => [row["eid"], { projectedVacationBalance: row["projected vacation hours"] }])
    );

    const updatedDeclarations = props.declarations.map((eachEmp) => ({
      ...eachEmp,
      ...(eidToBalanceMap[eachEmp.eid] || {}),
    }));

    props.setDeclarations(updatedDeclarations);
    setShowBalanceImportSuccessDialog(true);
  };

  const getToolTipTitle = () => {
    if (props.workgroup) {
      if (getUnionContractGivenWorkgroup(props.workgroup) === TWU_CONTRACT) {
        return "Import a CSV of the projected # of vacation and FTO balances";
      } else if (getUnionContractGivenWorkgroup(props.workgroup) === IAM_CONTRACT) {
        return "Import a CSV of the projected # of vacation balances";
      } else return "";
    }
    return "";
  };

  return (
    <>
      <Tooltip title={getToolTipTitle()} placement="bottom" arrow>
        {isBalanceImporting ? (
          <Button
            data-testid="declaration-loading"
            variant="outlined"
            color="primary"
            sx={{ marginRight: "15px", height: "40px" }}
          >
            <CircularProgress size={24} /> IMPORT BALANCES
          </Button>
        ) : (
          <Button
            data-testid="declaration-import-balance-button"
            variant="outlined"
            color="primary"
            sx={{ marginRight: "15px", height: "40px" }}
            disabled={isBalanceImportButtonDisabled(
              props?.declarations,
              props.declarationYear,
              props.station,
              props.declarationWindowDates[0],
              props.declarationWindowDates[1]
            )}
            startIcon={<DownloadingIcon />}
            component="label"
          >
            IMPORT BALANCES
            <input type="file" accept=".csv" hidden onChange={handleBalancesImport} />
          </Button>
        )}
      </Tooltip>
      <WarningDialog
        isDialogOpen={!!balancesCSVErrors}
        handleClickDialogClose={() => setBalancesCSVErrors(null)}
        header="Unable to Import"
        message={balancesCSVErrors?.join("\n") || "default message"}
        severity={"error"}
        width="auto"
      />
      <SuccessDialog
        isSuccessDialogOpen={showBalanceImportSuccessDialog}
        title={"Success"}
        message={
          "Balance import request submitted successfully for processing. Refresh your screen to view the updates."
        }
        handleOKClick={() => setShowBalanceImportSuccessDialog(false)}
      />
    </>
  );
};

DeclarationBalanceImport.propTypes = {
  workgroup: PropTypes.string.isRequired,
  declarations: PropTypes.array.isRequired,
  setDeclarations: PropTypes.func.isRequired,
  declarationWindowDates: PropTypes.array.isRequired,
  station: PropTypes.string.isRequired,
  declarationYear: PropTypes.string.isRequired,
};

export default DeclarationBalanceImport;
